import React, { useMemo } from 'react';
import { Target, TrendingUp, Calculator, Wallet, Activity } from 'lucide-react';
import { parseQuoteObject, quoteLabour } from '../quoteCalculations';

interface FinancialPacingProps {
  quotes: any[];
  annualTarget?: number;
}

export const FinancialPacing = ({ quotes, annualTarget = 500000 }: FinancialPacingProps) => {
  
  const chartData = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    let fyStartYear = today.getFullYear();
    if (today.getMonth() < 3) fyStartYear -= 1; 
    const fyStartDate = new Date(fyStartYear, 3, 1); 

    const getSalesDate = (q: any) => {
      const bookingDate = new Date(q.createdAt || q.created_at || q.updated_at || new Date());
      if (bookingDate < fyStartDate) {
          return new Date(fyStartDate);
      }
      return bookingDate;
    };

    const fyQuotes = quotes.filter(q => {
        const d = getSalesDate(q);
        return d >= fyStartDate && d <= today;
    });

    const approvedQuotes = quotes.filter(q => q.status === 'APPROVED' || String(q.status) === 'COMPLETED' || String(q.status) === 'COMPLETE');
    const pendingQuotes = fyQuotes.filter(q => ['SENT', 'PROPOSAL'].includes(String(q.status).toUpperCase()));

    const dailyData = [];
    let runningActual = 0;
    let runningPending = 0;
    
    let currentDate = new Date(fyStartDate);
    
    while (currentDate <= today) {
        const actualsToday = approvedQuotes.filter(q => {
            const d = getSalesDate(q);
            return d.getDate() === currentDate.getDate() && d.getMonth() === currentDate.getMonth() && d.getFullYear() === currentDate.getFullYear();
        }).reduce((sum, q) => sum + (Number(q.subtotal || q.amount || q.value) || 0), 0);

        const pendingToday = pendingQuotes.filter(q => {
            const d = getSalesDate(q);
            return d.getDate() === currentDate.getDate() && d.getMonth() === currentDate.getMonth() && d.getFullYear() === currentDate.getFullYear();
        }).reduce((sum, q) => sum + (Number(q.subtotal || q.amount || q.value) || 0), 0);

        runningActual += actualsToday;
        runningPending += pendingToday;

        dailyData.push({
            date: new Date(currentDate),
            actual: runningActual,
            potential: runningActual + runningPending
        });

        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dailyData;
  }, [quotes, annualTarget]);

  // ⚡ THE NEW PROFITABILITY PULSE ENGINE ⚡
  const profitMetrics = useMemo(() => {
    const today = new Date();
    let fyStartYear = today.getFullYear();
    if (today.getMonth() < 3) fyStartYear -= 1; 
    const fyStartDate = new Date(fyStartYear, 3, 1); 

    // Find all won quotes this year
    const ytdApprovedQuotes = quotes.filter(q => {
        const bookingDate = new Date(q.createdAt || q.created_at || q.updated_at || new Date());
        const d = bookingDate < fyStartDate ? new Date(fyStartDate) : bookingDate;
        const isYtd = d >= fyStartDate && d <= today;
        const isWon = ['APPROVED', 'COMPLETED', 'COMPLETE'].includes(String(q.status).toUpperCase());
        return isYtd && isWon;
    });

    // Smart Filter: Only calculate margin on quotes that actually have the new Profit Data attached!
    const trackedQuotes = ytdApprovedQuotes.filter(q => {
        return parseQuoteObject(q.logistics).internalCosts || q.internal_costs;
    });

    let totalRev = 0;
    let totalCost = 0;

    trackedQuotes.forEach(q => {
        const toNum = (val: any) => isNaN(parseFloat(val)) ? 0 : parseFloat(val);
        const subtotal = toNum(q.subtotal || q.amount || q.value || 0);

        // Extract the Trojan Horse data
        const internalCosts = parseQuoteObject(q.internal_costs ?? parseQuoteObject(q.logistics).internalCosts);

        // Recalculate auto-labor cost
        const autoLaborCost = quoteLabour(q).cost;

        // Tally all costs
        const quoteCost = autoLaborCost + 
            toNum(internalCosts.designPreProduction) + 
            toNum(internalCosts.logisticsTravel) + 
            toNum(internalCosts.physicalMaterials) + 
            toNum(internalCosts.digitalSoftware);

        totalRev += subtotal;
        totalCost += quoteCost;
    });

    const grossProfit = totalRev - totalCost;
    const marginPercent = totalRev > 0 ? (grossProfit / totalRev) * 100 : 0;

    return { 
        grossProfit, 
        totalCost, 
        marginPercent,
        trackedJobsCount: trackedQuotes.length
    };
  }, [quotes]);

  // ⚡ PACING VISUAL ENGINE 
  const width = 1200;
  const height = 240;
  
  const currentActual = chartData.length > 0 ? chartData[chartData.length - 1].actual : 0;
  const currentPotential = chartData.length > 0 ? chartData[chartData.length - 1].potential : 0;

  const maxAxisValue = Math.max(annualTarget, currentPotential) * 1.1; 
  
  const getX = (index: number) => (index / 365) * width;
  const getY = (value: number) => height - ((value / maxAxisValue) * height);
  
  let actualPoints = '';
  let potentialPoints = '';
  
  if (chartData.length > 0) {
      chartData.forEach((d, i) => {
          actualPoints += `${getX(i)},${getY(d.actual)} `;
          potentialPoints += `${getX(i)},${getY(d.potential)} `;
      });
  }

  const isAhead = currentActual >= (annualTarget / 365) * chartData.length;

  return (
    <div className="flex flex-col gap-6">
      {/* 1. FINANCIAL PACING CHART */}
      <div className="bg-slate-900/60 border border-white/10 rounded-[24px] p-6 md:p-8 shadow-xl w-full">
        <div className="flex justify-between items-start mb-10">
          <div>
            <h3 className="text-white font-bold text-xl md:text-2xl flex items-center gap-3">
              <TrendingUp size={24} className={isAhead ? "text-green-400" : "text-brand-neonBlue"} /> Financial Pacing
            </h3>
            <p className="text-slate-500 text-[10px] md:text-xs font-black uppercase tracking-widest mt-2">Daily Cumulative (Apr 1 — Mar 31)</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] md:text-xs font-black uppercase tracking-widest text-slate-500 mb-1">Annual Target</p>
            <p className="text-white font-mono font-bold text-xl md:text-2xl">${annualTarget.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
          </div>
        </div>

        <div className="relative w-full h-[200px] md:h-[240px] group">
          <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="w-full h-full overflow-visible">
            <line x1="0" y1={height} x2={width} y2={getY(annualTarget)} fill="none" stroke="#64748b" strokeWidth="2" strokeDasharray="8 8" opacity="0.4" />
            {chartData.length > 0 && <polyline points={potentialPoints} fill="none" stroke="#a855f7" strokeWidth="2" strokeDasharray="4 4" opacity="0.8" />}
            {chartData.length > 0 && <polyline points={actualPoints} fill="none" stroke={isAhead ? "#4ade80" : "#00f3ff"} strokeWidth="3" />}
            {chartData.length > 0 && <circle cx={getX(chartData.length - 1)} cy={getY(currentActual)} r="6" fill={isAhead ? "#4ade80" : "#00f3ff"} />}
            <defs>
              <linearGradient id="blueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={isAhead ? "#4ade80" : "#00f3ff"} stopOpacity="1" />
                <stop offset="100%" stopColor={isAhead ? "#4ade80" : "#00f3ff"} stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute top-full left-0 right-0 mt-4 flex justify-between px-1">
              {['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'].map((m, i) => (
                  <span key={i} className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-500">{m}</span>
              ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 md:gap-8 mt-12 pt-6 border-t border-white/5">
          <div className="flex items-center gap-2">
            <div className={`w-3 md:w-4 h-3 md:h-4 rounded-full ${isAhead ? 'bg-green-400' : 'bg-brand-neonBlue shadow-neon'}`}></div>
            <span className="text-xs md:text-sm font-bold text-white">Actuals (${currentActual.toLocaleString(undefined, {maximumFractionDigits: 0})})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 md:w-4 h-3 md:h-4 rounded-full border-2 border-brand-neonPink border-dashed"></div>
            <span className="text-xs md:text-sm font-bold text-slate-300">Pipeline (${(currentPotential - currentActual).toLocaleString(undefined, {maximumFractionDigits: 0})})</span>
          </div>
          <div className="flex items-center gap-2 ml-auto bg-white/5 px-4 py-2 rounded-xl">
            <Target size={16} className="text-slate-400" />
            <span className="text-xs md:text-sm font-bold text-white">{annualTarget > 0 ? Math.round((currentActual / annualTarget) * 100) : 0}% to Goal</span>
          </div>
        </div>
      </div>

      {/* 2. PROFITABILITY PULSE WIDGET */}
      <div className="bg-slate-900/60 border border-white/10 rounded-[24px] p-6 shadow-xl w-full">
          <div className="flex justify-between items-center mb-6">
              <h3 className="text-white font-bold text-lg flex items-center gap-3">
                  <Activity size={20} className="text-yellow-400" /> YTD Profitability Pulse
              </h3>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 bg-white/5 px-3 py-1 rounded-md">
                  Based on {profitMetrics.trackedJobsCount} Tracked Jobs
              </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Gross Profit */}
              <div className="bg-black/40 border border-white/5 rounded-2xl p-5 relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-20"><Wallet size={48} className="text-white"/></div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 relative z-10">Realized Gross Profit</p>
                  <p className="text-2xl font-mono font-bold text-white relative z-10">
                      ${profitMetrics.grossProfit.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
              </div>

              {/* Total Costs */}
              <div className="bg-black/40 border border-white/5 rounded-2xl p-5 relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-20"><Calculator size={48} className="text-white"/></div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 relative z-10">Internal OpEx Spend</p>
                  <p className="text-2xl font-mono font-bold text-slate-300 relative z-10">
                      ${profitMetrics.totalCost.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
              </div>

              {/* Avg Margin % */}
              <div className={`border rounded-2xl p-5 relative overflow-hidden ${
                  profitMetrics.marginPercent >= 45 ? 'bg-green-500/10 border-green-500/30' : 
                  profitMetrics.marginPercent >= 30 ? 'bg-yellow-500/10 border-yellow-500/30' : 
                  'bg-red-500/10 border-red-500/30'
              }`}>
                  <p className={`text-[10px] font-black uppercase tracking-widest mb-2 relative z-10 ${
                      profitMetrics.marginPercent >= 45 ? 'text-green-500' : 
                      profitMetrics.marginPercent >= 30 ? 'text-yellow-500' : 
                      'text-red-500'
                  }`}>
                      Avg. Profit Margin
                  </p>
                  <p className={`text-3xl font-mono font-bold relative z-10 ${
                      profitMetrics.marginPercent >= 45 ? 'text-green-400' : 
                      profitMetrics.marginPercent >= 30 ? 'text-yellow-400' : 
                      'text-red-400'
                  }`}>
                      {profitMetrics.marginPercent.toFixed(1)}%
                  </p>
              </div>
          </div>
      </div>
    </div>
  );
};
